/* eslint-disable @typescript-eslint/no-explicit-any */

import type {CSSObject, DefaultTheme, Interpolation, ThemeProps} from 'styled-components'

import {
  parse as parseBoxShadow,
  stringify as convertBoxShadow,
  isFluid as isBoxShadowFluid,
} from './boxShadow'
import normalizeBreakpointsArray from './normalizeBreakpointsArray'

/**
 * List of css properties that need to be converted to vw units
 */
const fluidProps = [
  'padding',
  'margin',
  'paddingLeft',
  'marginLeft',
  'paddingRight',
  'marginRight',
  'paddingBottom',
  'marginBottom',
  'paddingTop',
  'marginTop',
  'width',
  'minWidth',
  'maxWidth',
  'height',
  'maxHeight',
  'borderRadius',
  'top',
  'right',
  'bottom',
  'left',
  'fontSize',
  'lineHeight',
  'letterSpacing',
  'gap',
  'rowGap',
  'columnGap',
  'gridGap',
  'gridRowGap',
  'gridColumnGap',
  'gridAutoRows',
  'flexBasis',
  'outlineOffset',
  'outlineWidth',
]

const colorProps = ['background', 'backgroundColor', 'color', 'borderColor', 'fill', 'stroke']

const isFluidProp = (key: string, value: any): boolean =>
  fluidProps.includes(key) && value !== null && value !== undefined && !isNaN(value) && value != 0

const fallbackMultiplier = (value: number): string => `${value}px`

const normalizeValue = (key: string, value: any, theme: DefaultTheme, media?: string) => {
  // Support color tokens
  if (colorProps.includes(key)) {
    return theme.colors[value] || value
  }

  if (isFluidProp(key, value)) {
    return getMultiplier(theme, media)(value)
  }

  if (key === 'boxShadow' && value) {
    const options = parseBoxShadow(value)
    return convertBoxShadow(options, getMultiplier(theme, media))
  }

  return value
}

const getMultiplier = (theme: DefaultTheme, media?: string) =>
  theme.disableScaling || !media
    ? fallbackMultiplier
    : theme.multipliers[media] || fallbackMultiplier

function convertToBreakpoints(
  values: Record<string, unknown>,
  theme: DefaultTheme,
  media?: string,
) {
  const rules: CSSObject = {}

  const writeRule = (key: string, value: unknown, media?: string) => {
    if (value === null || value === undefined) {
      return
    }

    if (!media) {
      rules[key] = normalizeValue(key, value, theme, media)
    } else {
      const mediaQuery = `@media ${theme.mediaQueries[media]}`

      if (typeof rules[mediaQuery] === 'undefined') {
        rules[mediaQuery] = {}
      }

      const rule = rules[mediaQuery] || {}
      rule[key] = normalizeValue(key, value, theme, media)
    }
  }

  Object.entries(values).forEach(([key, val]: Array<any>) => {
    if (Object.prototype.toString.call(val) === '[object Object]') {
      rules[key] = convertToBreakpoints(val, theme)
      return
    }

    let cleanValue = val

    if (Array.isArray(val)) {
      cleanValue = normalizeBreakpointsArray(val, theme)
    } else if (isFluidProp(key, val)) {
      // If number is passed and it's a fluid prop than convert to breakpoints with single value
      cleanValue = [...new Array(theme.viewportsArray.length)].map(() => val)
    } else if (key === 'boxShadow' && typeof val === 'string' && isBoxShadowFluid(val)) {
      cleanValue = [...new Array(theme.viewportsArray.length)].map(() => val)
    }

    if (media) {
      const mediaQuery = theme.mediaQueries[media]

      const arrIndex = theme.viewportsArray.indexOf(media)

      if (!mediaQuery || arrIndex === -1) {
        console.warn('Invalid media query passed to normalizedStyles', media)
        return
      }

      writeRule(key, Array.isArray(val) ? val[arrIndex] : val, media)
    } else if (Array.isArray(cleanValue)) {
      cleanValue.forEach((val, i) => {
        writeRule(key, val, theme.viewportsArray[i])
      })
    } else {
      writeRule(key, cleanValue)
    }
  })

  return rules
}

type NormalizeStyles = (
  values: any,
  media?: string,
) => (props: ThemeProps<DefaultTheme>) => Interpolation<any> | null

const normalizeStyles: NormalizeStyles = (values, media) => ({theme}) => {
  return values ? convertToBreakpoints(values, theme, media) : null
}

export default normalizeStyles
